import {
  type LanguageJourney,
  type LanguageJourneyChapter,
  type LanguageJourneyProgress,
  getLanguageJourneyByCode,
  getLanguageJourneyCatalog,
} from "@/lib/languageJourney";
import { z } from "zod";

export const SPANISH_JOURNEY_PLAN_VERSION = 1;
export const SPANISH_JOURNEY_LANGUAGE_CODE = "es-ES";
export const SPANISH_JOURNEY_PROGRESS_NAMESPACE = "spanish-v3";

export const SpanishJourneyGoalSchema = z.enum([
  "everyday",
  "travel",
  "work",
  "relationships",
  "media",
  "study",
]);

export const SpanishJourneyAbilitySchema = z.enum([
  "starting_out",
  "basic_building_blocks",
  "everyday_conversations",
  "confident_conversations",
  "advanced_expression",
]);

export const SpanishJourneyTeachingApproachSchema = z.enum([
  "guided_discovery",
  "explain_then_practice",
  "examples_first",
  "challenge_me",
]);

export const SpanishJourneyStuckStrategySchema = z.enum([
  "hint",
  "smaller_steps",
  "another_example",
  "realistic_situation",
  "retry_before_help",
]);

export const SpanishJourneyCorrectionTimingSchema = z.enum([
  "immediate",
  "after_attempt",
  "only_blocking",
]);

export const SpanishJourneyLanguageSupportSchema = z.enum([
  "mostly_spanish",
  "balanced",
  "more_english",
]);

export const SpanishJourneyChallengeLevelSchema = z.enum(["comfortable", "focused", "intensive"]);

export const SpanishJourneyTimeSchema = z.enum(["two_weeks", "one_month", "six_weeks", "flexible"]);

export const SpanishJourneyGoalProfileSchema = z
  .object({
    goal: SpanishJourneyGoalSchema,
    ability: SpanishJourneyAbilitySchema,
    startingChapterId: z.string().trim().min(1).optional(),
    teachingApproach: SpanishJourneyTeachingApproachSchema.default("guided_discovery"),
    stuckStrategy: SpanishJourneyStuckStrategySchema.default("hint"),
    correctionTiming: SpanishJourneyCorrectionTimingSchema.default("after_attempt"),
    languageSupport: SpanishJourneyLanguageSupportSchema.default("balanced"),
    challengeLevel: SpanishJourneyChallengeLevelSchema.default("focused"),
    timeAvailable: SpanishJourneyTimeSchema,
    goalDetail: z.string().trim().max(160).optional().default(""),
  })
  .superRefine((profile, context) => {
    if (!profile.startingChapterId) return;
    const journey = getSpanishJourney();
    const startIndex = journey.chapters.findIndex(
      (chapter) => chapter.id === profile.startingChapterId
    );
    const requiredSessions = PLAN_SHAPE_BY_TIME[profile.timeAvailable].sessions;
    if (startIndex < 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startingChapterId"],
        message: "Choose a reviewed Spanish chapter.",
      });
    } else if (journey.chapters.length - startIndex < requiredSessions) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startingChapterId"],
        message: `Choose a chapter with room for ${requiredSessions} sessions.`,
      });
    }
  });

export type SpanishJourneyGoalProfile = z.infer<typeof SpanishJourneyGoalProfileSchema>;

export const SpanishJourneyPlanItemSchema = z.object({
  chapterId: z.string().min(1),
  milestone: z.string().trim().min(1).max(80),
  reason: z.string().trim().min(1).max(240),
  applicationContext: z.string().trim().min(1).max(180),
});

export const SpanishJourneyPlanSchema = z.object({
  version: z.literal(SPANISH_JOURNEY_PLAN_VERSION),
  languageCode: z.literal(SPANISH_JOURNEY_LANGUAGE_CODE),
  progressNamespace: z.literal(SPANISH_JOURNEY_PROGRESS_NAMESPACE),
  catalogVersion: z.number().int().positive(),
  courseVersion: z.number().int().positive(),
  title: z.string().trim().min(1).max(80),
  estimatedSessions: z.number().int().min(6).max(10),
  estimatedWeeks: z.number().int().min(1).max(12),
  status: z.enum(["draft", "active"]),
  profile: SpanishJourneyGoalProfileSchema,
  items: z.array(SpanishJourneyPlanItemSchema).min(6).max(10),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export type SpanishJourneyPlan = z.infer<typeof SpanishJourneyPlanSchema>;
export type SpanishJourneyPlanItem = z.infer<typeof SpanishJourneyPlanItemSchema>;

export const SPANISH_GOAL_LABELS: Record<SpanishJourneyGoalProfile["goal"], string> = {
  everyday: "everyday conversations",
  travel: "travel confidently",
  work: "work in Spanish",
  relationships: "connect with people",
  media: "understand Spanish media",
  study: "study in Spanish",
};

const SPANISH_PLAN_TITLE_LABELS: Record<SpanishJourneyGoalProfile["goal"], string> = {
  everyday: "Everyday Spanish",
  travel: "Spanish for confident travel",
  work: "Spanish for work",
  relationships: "Spanish for real connections",
  media: "Spanish through media",
  study: "Spanish for study",
};

export const SPANISH_TEACHING_APPROACH_LABELS: Record<
  SpanishJourneyGoalProfile["teachingApproach"],
  string
> = {
  guided_discovery: "Guide me with questions and clues",
  explain_then_practice: "Explain briefly, then practise",
  examples_first: "Show examples before explaining",
  challenge_me: "Challenge me with less help",
};

export const SPANISH_STUCK_STRATEGY_LABELS: Record<
  SpanishJourneyGoalProfile["stuckStrategy"],
  string
> = {
  hint: "Give me one useful hint",
  smaller_steps: "Break it into smaller steps",
  another_example: "Show me another example",
  realistic_situation: "Put it in a realistic situation",
  retry_before_help: "Let me retry before helping",
};

export const SPANISH_CORRECTION_TIMING_LABELS: Record<
  SpanishJourneyGoalProfile["correctionTiming"],
  string
> = {
  immediate: "Correct important errors immediately",
  after_attempt: "Let me finish, then correct me",
  only_blocking: "Correct only errors that block meaning",
};

export const SPANISH_LANGUAGE_SUPPORT_LABELS: Record<
  SpanishJourneyGoalProfile["languageSupport"],
  string
> = {
  mostly_spanish: "Use mostly Spanish",
  balanced: "Balance Spanish with English support",
  more_english: "Use more English support",
};

export const SPANISH_CHALLENGE_LEVEL_LABELS: Record<
  SpanishJourneyGoalProfile["challengeLevel"],
  string
> = {
  comfortable: "Keep the pressure comfortable",
  focused: "Keep me focused and moving",
  intensive: "Push me with a faster, harder session",
};

const START_CHAPTER_BY_ABILITY: Record<SpanishJourneyGoalProfile["ability"], number> = {
  starting_out: 1,
  basic_building_blocks: 18,
  everyday_conversations: 36,
  confident_conversations: 58,
  advanced_expression: 78,
};

const PLAN_SHAPE_BY_TIME: Record<
  SpanishJourneyGoalProfile["timeAvailable"],
  { sessions: number; weeks: number }
> = {
  two_weeks: { sessions: 8, weeks: 4 },
  one_month: { sessions: 8, weeks: 4 },
  six_weeks: { sessions: 8, weeks: 4 },
  flexible: { sessions: 8, weeks: 4 },
};

export function isSpanishJourneyCode(languageCode: string | null | undefined): boolean {
  if (!languageCode) return false;
  return Boolean(
    getLanguageJourneyByCode(languageCode)?.progressNamespace === SPANISH_JOURNEY_PROGRESS_NAMESPACE
  );
}

export function getSpanishJourney(): LanguageJourney {
  const journey = getLanguageJourneyByCode(SPANISH_JOURNEY_LANGUAGE_CODE);
  if (!journey || journey.progressNamespace !== SPANISH_JOURNEY_PROGRESS_NAMESPACE) {
    throw new Error("The reviewed Spanish Journey catalog is unavailable.");
  }
  return journey;
}

export function getSpanishJourneyPlanShape(profile: SpanishJourneyGoalProfile) {
  return PLAN_SHAPE_BY_TIME[profile.timeAvailable];
}

export function getSpanishJourneyAbilityForChapter(
  chapter: LanguageJourneyChapter
): SpanishJourneyGoalProfile["ability"] {
  if (chapter.chapterNumber >= START_CHAPTER_BY_ABILITY.advanced_expression) {
    return "advanced_expression";
  }
  if (chapter.chapterNumber >= START_CHAPTER_BY_ABILITY.confident_conversations) {
    return "confident_conversations";
  }
  if (chapter.chapterNumber >= START_CHAPTER_BY_ABILITY.everyday_conversations) {
    return "everyday_conversations";
  }
  if (chapter.chapterNumber >= START_CHAPTER_BY_ABILITY.basic_building_blocks) {
    return "basic_building_blocks";
  }
  return "starting_out";
}

export function getSpanishJourneyStartingChapter(
  profile: SpanishJourneyGoalProfile
): LanguageJourneyChapter {
  const journey = getSpanishJourney();
  const selected = profile.startingChapterId
    ? journey.chapters.find((chapter) => chapter.id === profile.startingChapterId)
    : null;
  const fallbackNumber = START_CHAPTER_BY_ABILITY[profile.ability];
  const fallback = journey.chapters.find((chapter) => chapter.chapterNumber === fallbackNumber);
  if (!selected && !fallback) throw new Error("The reviewed Spanish starting chapter is missing.");
  return selected || fallback!;
}

export function getSpanishJourneyCandidateChapters(
  profile: SpanishJourneyGoalProfile
): LanguageJourneyChapter[] {
  const journey = getSpanishJourney();
  const startingChapter = getSpanishJourneyStartingChapter(profile);
  const startIndex = journey.chapters.findIndex((chapter) => chapter.id === startingChapter.id);
  const { sessions } = getSpanishJourneyPlanShape(profile);
  const windowSize = Math.min(sessions + 4, journey.chapters.length - startIndex);
  return journey.chapters.slice(Math.max(0, startIndex), Math.max(0, startIndex) + windowSize);
}

function evenlySelectChapters(
  candidates: LanguageJourneyChapter[],
  count: number
): LanguageJourneyChapter[] {
  if (candidates.length <= count) return candidates.slice(0, count);
  const maxIndex = candidates.length - 1;
  const indices = new Set<number>([0]);
  for (let index = 1; index < count; index += 1) {
    indices.add(Math.round((index * maxIndex) / (count - 1)));
  }
  for (let index = 0; indices.size < count && index < candidates.length; index += 1) {
    indices.add(index);
  }
  return [...indices]
    .sort((a, b) => a - b)
    .slice(0, count)
    .map((index) => candidates[index]);
}

function fallbackMilestone(chapter: LanguageJourneyChapter): string {
  return chapter.unitTitle || chapter.cefrLevelTitle || `${chapter.cefrLevel} speaking control`;
}

function fallbackReason(
  chapter: LanguageJourneyChapter,
  profile: SpanishJourneyGoalProfile
): string {
  return `Because you said you want to ${SPANISH_GOAL_LABELS[profile.goal]}, this chapter builds ${
    chapter.newConcept || chapter.objective.toLowerCase()
  } while Chicky teaches it through ${SPANISH_TEACHING_APPROACH_LABELS[
    profile.teachingApproach
  ].toLowerCase()}.`;
}

function fallbackApplicationContext(profile: SpanishJourneyGoalProfile): string {
  const detail = profile.goalDetail?.trim();
  return detail
    ? `Apply this chapter to: ${detail}`
    : `Apply this chapter to ${SPANISH_GOAL_LABELS[profile.goal]}.`;
}

export function buildFallbackSpanishJourneyPlan(
  rawProfile: SpanishJourneyGoalProfile,
  now = new Date()
): SpanishJourneyPlan {
  const profile = SpanishJourneyGoalProfileSchema.parse(rawProfile);
  const journey = getSpanishJourney();
  const catalog = getLanguageJourneyCatalog();
  const shape = getSpanishJourneyPlanShape(profile);
  const selected = evenlySelectChapters(
    getSpanishJourneyCandidateChapters(profile),
    shape.sessions
  );
  const timestamp = now.toISOString();

  return {
    version: SPANISH_JOURNEY_PLAN_VERSION,
    languageCode: SPANISH_JOURNEY_LANGUAGE_CODE,
    progressNamespace: SPANISH_JOURNEY_PROGRESS_NAMESPACE,
    catalogVersion: catalog.version,
    courseVersion: journey.courseVersion || 1,
    title: SPANISH_PLAN_TITLE_LABELS[profile.goal],
    estimatedSessions: selected.length,
    estimatedWeeks: shape.weeks,
    status: "draft",
    profile,
    items: selected.map((chapter) => ({
      chapterId: chapter.id,
      milestone: fallbackMilestone(chapter),
      reason: fallbackReason(chapter, profile),
      applicationContext: fallbackApplicationContext(profile),
    })),
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

type RawGeneratedPlan = {
  title?: unknown;
  items?: unknown;
};

export function normalizeGeneratedSpanishJourneyPlan(
  raw: RawGeneratedPlan,
  rawProfile: SpanishJourneyGoalProfile,
  now = new Date()
): SpanishJourneyPlan {
  const fallback = buildFallbackSpanishJourneyPlan(rawProfile, now);
  const candidates = getSpanishJourneyCandidateChapters(fallback.profile);
  const candidateById = new Map(candidates.map((chapter) => [chapter.id, chapter]));
  const rawItems = Array.isArray(raw?.items) ? raw.items : [];
  const rawById = new Map<string, Record<string, unknown>>();

  for (const value of rawItems) {
    if (!value || typeof value !== "object") continue;
    const item = value as Record<string, unknown>;
    const chapterId = typeof item.chapterId === "string" ? item.chapterId : "";
    if (candidateById.has(chapterId) && !rawById.has(chapterId)) rawById.set(chapterId, item);
  }

  const requestedIds = [...rawById.keys()];
  const firstCandidateId = candidates[0]?.id;
  if (firstCandidateId && !requestedIds.includes(firstCandidateId))
    requestedIds.unshift(firstCandidateId);

  const fallbackIds = fallback.items.map((item) => item.chapterId);
  const mergedIds = [...new Set([...requestedIds, ...fallbackIds])]
    .filter((id) => candidateById.has(id))
    .sort(
      (a, b) =>
        (candidateById.get(a)?.chapterNumber || 0) - (candidateById.get(b)?.chapterNumber || 0)
    )
    .slice(0, fallback.estimatedSessions);

  const selectedChapters = mergedIds.map((id) => candidateById.get(id)!);
  const hasUnsafeGap = selectedChapters.some((chapter, index) => {
    if (index === 0) return false;
    return chapter.chapterNumber - selectedChapters[index - 1].chapterNumber > 2;
  });
  const finalIds =
    selectedChapters.length === fallback.estimatedSessions && !hasUnsafeGap
      ? mergedIds
      : fallbackIds;

  const title = typeof raw?.title === "string" ? raw.title.trim().slice(0, 80) : "";
  return SpanishJourneyPlanSchema.parse({
    ...fallback,
    title: title || fallback.title,
    items: finalIds.map((chapterId) => {
      const chapter = candidateById.get(chapterId)!;
      const generated = rawById.get(chapterId);
      const milestone =
        typeof generated?.milestone === "string" ? generated.milestone.trim().slice(0, 80) : "";
      const reason =
        typeof generated?.reason === "string" ? generated.reason.trim().slice(0, 240) : "";
      const applicationContext =
        typeof generated?.applicationContext === "string"
          ? generated.applicationContext.trim().slice(0, 180)
          : "";
      return {
        chapterId,
        milestone: milestone || fallbackMilestone(chapter),
        reason: reason || fallbackReason(chapter, fallback.profile),
        applicationContext: applicationContext || fallbackApplicationContext(fallback.profile),
      };
    }),
  });
}

export function validateSpanishJourneyPlan(plan: SpanishJourneyPlan): SpanishJourneyPlan {
  const parsed = SpanishJourneyPlanSchema.parse(plan);
  const journey = getSpanishJourney();
  const catalog = getLanguageJourneyCatalog();
  if (parsed.catalogVersion !== catalog.version || parsed.courseVersion !== journey.courseVersion) {
    throw new Error("This plan was created for an older Spanish Journey catalog.");
  }

  const candidates = getSpanishJourneyCandidateChapters(parsed.profile);
  const candidateNumbers = new Map(
    candidates.map((chapter) => [chapter.id, chapter.chapterNumber])
  );
  const numbers = parsed.items.map((item) => candidateNumbers.get(item.chapterId));
  if (numbers.some((number) => number === undefined)) {
    throw new Error("The plan contains a chapter outside its reviewed starting window.");
  }
  if (new Set(parsed.items.map((item) => item.chapterId)).size !== parsed.items.length) {
    throw new Error("The plan contains a duplicate chapter.");
  }
  if (numbers[0] !== candidates[0]?.chapterNumber) {
    throw new Error("The plan must start at the reviewed entry chapter for this level.");
  }
  for (let index = 1; index < numbers.length; index += 1) {
    const previous = numbers[index - 1]!;
    const current = numbers[index]!;
    if (current <= previous || current - previous > 2) {
      throw new Error("The plan chapter order would break the reviewed progression.");
    }
  }
  return parsed;
}

export function getSpanishJourneyPlanRecommendation(
  plan: SpanishJourneyPlan | null,
  progress: LanguageJourneyProgress
): LanguageJourneyChapter | null {
  if (!plan) return null;
  const journey = getSpanishJourney();
  const completed = new Set(progress.completedChapterIds);
  const nextItem = plan.items.find((item) => !completed.has(item.chapterId));
  if (!nextItem) return null;
  return journey.chapters.find((chapter) => chapter.id === nextItem.chapterId) || null;
}

export function shouldApplySpanishJourneyRecommendation({
  selectedChapterId,
  defaultChapterId,
  explicitlySelectedChapterId,
}: {
  selectedChapterId: string | null;
  defaultChapterId: string | null;
  explicitlySelectedChapterId: string | null;
}): boolean {
  const hasExplicitSelection =
    Boolean(selectedChapterId) && explicitlySelectedChapterId === selectedChapterId;
  return !hasExplicitSelection && (!selectedChapterId || selectedChapterId === defaultChapterId);
}

export function getSpanishJourneyPlanCompletedCount(
  plan: SpanishJourneyPlan,
  progress: LanguageJourneyProgress
): number {
  const completed = new Set(progress.completedChapterIds);
  return plan.items.filter((item) => completed.has(item.chapterId)).length;
}

export function getSpanishJourneyReplacementOptions(
  plan: SpanishJourneyPlan,
  itemIndex: number
): LanguageJourneyChapter[] {
  if (itemIndex <= 0 || itemIndex >= plan.items.length) return [];
  const candidates = getSpanishJourneyCandidateChapters(plan.profile);
  const used = new Set(plan.items.map((item) => item.chapterId));
  return candidates.filter((chapter) => {
    if (used.has(chapter.id)) return false;
    const nextItems = plan.items.map((item, index) =>
      index === itemIndex
        ? {
            ...item,
            chapterId: chapter.id,
            milestone: fallbackMilestone(chapter),
            reason: fallbackReason(chapter, plan.profile),
          }
        : item
    );
    try {
      validateSpanishJourneyPlan({ ...plan, items: nextItems });
      return true;
    } catch {
      return false;
    }
  });
}

export function replaceSpanishJourneyPlanItem(
  plan: SpanishJourneyPlan,
  itemIndex: number,
  chapterId: string
): SpanishJourneyPlan {
  const chapter = getSpanishJourney().chapters.find((candidate) => candidate.id === chapterId);
  if (!chapter) throw new Error("That reviewed Spanish chapter is unavailable.");
  const next = {
    ...plan,
    updatedAt: new Date().toISOString(),
    items: plan.items.map((item, index) =>
      index === itemIndex
        ? {
            ...item,
            chapterId,
            milestone: fallbackMilestone(chapter),
            reason: fallbackReason(chapter, plan.profile),
          }
        : item
    ),
  };
  return validateSpanishJourneyPlan(next);
}

export function buildSpanishJourneyCoachingInstructions(
  plan: SpanishJourneyPlan | null,
  chapterId: string | null | undefined
): string {
  if (!plan || plan.status !== "active" || !chapterId) return "";
  const itemIndex = plan.items.findIndex((item) => item.chapterId === chapterId);
  if (itemIndex < 0) return "";

  const profile = plan.profile;
  return [
    "[SPANISH_JOURNEY_COACHING_V1]",
    "The reviewed chapter is authoritative; do not replace or skip its objective.",
    `Goal: ${SPANISH_GOAL_LABELS[profile.goal]}.`,
    `Plan position: session ${itemIndex + 1} of ${plan.items.length}.`,
    `Teach: ${SPANISH_TEACHING_APPROACH_LABELS[profile.teachingApproach]}.`,
    `If stuck: ${SPANISH_STUCK_STRATEGY_LABELS[profile.stuckStrategy]}.`,
    `Correct: ${SPANISH_CORRECTION_TIMING_LABELS[profile.correctionTiming]}.`,
    `Language: ${SPANISH_LANGUAGE_SUPPORT_LABELS[profile.languageSupport]}.`,
    `Challenge: ${SPANISH_CHALLENGE_LEVEL_LABELS[profile.challengeLevel]}.`,
    "Adapt examples and teaching moves only; require learner evidence for mastery.",
  ].join(" ");
}

export function extractJsonObject(value: string): RawGeneratedPlan {
  const trimmed = value.trim();
  const unfenced = trimmed
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();
  const start = unfenced.indexOf("{");
  const end = unfenced.lastIndexOf("}");
  if (start < 0 || end <= start) return {};
  try {
    return JSON.parse(unfenced.slice(start, end + 1)) as RawGeneratedPlan;
  } catch {
    return {};
  }
}

export function buildSpanishJourneyPlannerPrompt(profile: SpanishJourneyGoalProfile): string {
  const parsed = SpanishJourneyGoalProfileSchema.parse(profile);
  const candidates = getSpanishJourneyCandidateChapters(parsed);
  const shape = getSpanishJourneyPlanShape(parsed);
  const candidateText = candidates
    .map(
      (chapter) =>
        `${chapter.id} | Chapter ${chapter.chapterNumber} | ${chapter.title} | ${chapter.cefrLevel} | ${chapter.objective} | ${chapter.sampleChallenge}`
    )
    .join("\n");

  return `You design a short route through an already reviewed Spanish speaking course.

Learner answers:
- Goal: ${SPANISH_GOAL_LABELS[parsed.goal]}
- Existing ability: ${parsed.ability}
- Learner-selected starting chapter: ${getSpanishJourneyStartingChapter(parsed).id}
- Preferred teaching approach: ${SPANISH_TEACHING_APPROACH_LABELS[parsed.teachingApproach]}
- When stuck: ${SPANISH_STUCK_STRATEGY_LABELS[parsed.stuckStrategy]}
- Correction timing: ${SPANISH_CORRECTION_TIMING_LABELS[parsed.correctionTiming]}
- Language support: ${SPANISH_LANGUAGE_SUPPORT_LABELS[parsed.languageSupport]}
- Challenge level: ${SPANISH_CHALLENGE_LEVEL_LABELS[parsed.challengeLevel]}
- Route size: ${shape.sessions} reviewed sessions
- Optional detail: ${parsed.goalDetail || "none"}

Return JSON only with this shape:
{
  "title": "short personalized title",
  "items": [
    {
      "chapterId": "one exact allowed ID"
    }
  ]
}

Rules:
- Return exactly ${shape.sessions} items.
- Use only IDs listed below.
- Include ${candidates[0]?.id} first.
- Keep IDs in the listed order and never jump more than one omitted chapter.
- Do not invent lessons, IDs, prerequisites, mastery claims, or CEFR certification claims.
- Select the route only. Do not write learner-facing rationales or rewrite chapter descriptions.
- Keep the reviewed grammar/word-order progression intact.

Allowed reviewed chapters:
${candidateText}`;
}
